import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Breadcrumbs,
  Chip,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  MoreVert as MoreVertIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import {
  apiTokensApi,
  API_TOKEN_ABILITIES,
  type ApiToken,
  type ApiTokenAbility,
} from '@/api/apiTokens'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'

export function AdminApiTokensPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [tokens, setTokens] = useState<ApiToken[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuToken, setMenuToken] = useState<ApiToken | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [abilities, setAbilities] = useState<ApiTokenAbility[]>([])

  // Выпущенный plaintext: показывается один раз, до закрытия модалки
  const [issuedToken, setIssuedToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const loadTokens = async () => {
    setLoading(true)
    setError(null)
    try {
      setTokens(await apiTokensApi.getList())
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTokens()
  }, [])

  const openCreateDialog = () => {
    setName('')
    setAbilities([])
    setDialogError(null)
    setIssuedToken(null)
    setCopied(false)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    if (saving) return
    setDialogOpen(false)
    setIssuedToken(null)
  }

  const toggleAbility = (ability: ApiTokenAbility) => {
    setAbilities((prev) =>
      prev.includes(ability) ? prev.filter((a) => a !== ability) : [...prev, ability],
    )
  }

  const handleIssue = async () => {
    if (name.trim() === '' || abilities.length === 0) {
      setDialogError(t('admin.apiTokens.validationRequired') as string)
      return
    }
    setSaving(true)
    setDialogError(null)
    try {
      const { token } = await apiTokensApi.create({ name: name.trim(), abilities })
      setIssuedToken(token)
      await loadTokens()
    } catch (err) {
      setDialogError(getErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const handleCopy = async () => {
    if (!issuedToken) return
    try {
      await navigator.clipboard.writeText(issuedToken)
      setCopied(true)
    } catch {
      // clipboard может быть недоступен — токен виден, можно скопировать вручную
    }
  }

  const handleRevoke = async (token: ApiToken) => {
    if (!window.confirm(t('admin.apiTokens.revokeConfirm', { name: token.name }) as string)) return
    setError(null)
    setSuccess(null)
    try {
      await apiTokensApi.revoke(token.id)
      setSuccess(t('admin.apiTokens.revoked') as string)
      await loadTokens()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>, token: ApiToken) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setMenuToken(token)
  }

  // Токен обнуляется после закрытия меню, иначе пункт «Отозвать» моргает
  // состоянием disabled на анимации закрытия
  const closeMenu = () => {
    setMenuAnchor(null)
  }

  const statusChip = (token: ApiToken) => {
    if (token.revoked_at) {
      return <Chip size="small" color="default" label={t('admin.apiTokens.statusRevoked')} />
    }
    if (token.expires_at && new Date(token.expires_at) < new Date()) {
      return <Chip size="small" color="warning" label={t('admin.apiTokens.statusExpired')} />
    }
    return <Chip size="small" color="success" label={t('admin.apiTokens.statusActive')} />
  }

  const abilityChips = (token: ApiToken) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {token.abilities.map((ability) => (
        <Chip key={ability} size="small" variant="outlined" label={ability} />
      ))}
    </Box>
  )

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.administration')}</Typography>
          <Typography color="text.primary">{t('admin.apiTokens.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('admin.apiTokens.title')}
          </Typography>
          <PageHelpPopover pageKey="admin-api-tokens" />
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {t('admin.apiTokens.subtitleHint')}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={openCreateDialog} sx={{ height: 40 }}>
              {t('admin.apiTokens.create')}
            </Button>
          </Box>
        </Box>

        {loading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3].map((i) => (
                <Paper key={i} sx={{ p: 2 }}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={20} sx={{ mt: 0.5 }} />
                  <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1, mt: 1 }} />
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('admin.apiTokens.columnName')}</TableCell>
                    <TableCell>{t('admin.apiTokens.columnAbilities')}</TableCell>
                    <TableCell>{t('admin.apiTokens.columnStatus')}</TableCell>
                    <TableCell>{t('admin.apiTokens.columnLastUsed')}</TableCell>
                    <TableCell align="right">{t('admin.users.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width={140} /></TableCell>
                      <TableCell><Skeleton variant="text" width={160} /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : tokens.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">{t('admin.apiTokens.empty')}</Typography>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {tokens.map((token) => (
              <Paper key={token.id} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    #{token.id}
                  </Typography>
                  <IconButton size="small" onClick={(e) => openMenu(e, token)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography fontWeight={600}>{token.name}</Typography>
                <Box sx={{ mt: 1 }}>{abilityChips(token)}</Box>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {statusChip(token)}
                  {token.last_used_at && (
                    <Typography variant="caption" color="text.secondary">
                      {t('admin.apiTokens.columnLastUsed')}: {formatDateDMY(token.last_used_at)}
                    </Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('admin.apiTokens.columnName')}</TableCell>
                  <TableCell>{t('admin.apiTokens.columnAbilities')}</TableCell>
                  <TableCell>{t('admin.apiTokens.columnStatus')}</TableCell>
                  <TableCell>{t('admin.apiTokens.columnLastUsed')}</TableCell>
                  <TableCell align="right">{t('admin.users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tokens.map((token) => (
                  <TableRow key={token.id}>
                    <TableCell>{token.id}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{token.name}</Typography>
                      {token.created_at && (
                        <Typography variant="body2" color="text.secondary">
                          {t('admin.apiTokens.createdAt')}: {formatDateDMY(token.created_at)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{abilityChips(token)}</TableCell>
                    <TableCell>{statusChip(token)}</TableCell>
                    <TableCell>
                      {token.last_used_at ? formatDateDMY(token.last_used_at) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => openMenu(e, token)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          TransitionProps={{ onExited: () => setMenuToken(null) }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            disabled={Boolean(menuToken?.revoked_at)}
            onClick={() => {
              if (menuToken) handleRevoke(menuToken)
              closeMenu()
            }}
          >
            {t('admin.apiTokens.revoke')}
          </MenuItem>
        </Menu>

        <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>{t('admin.apiTokens.createTitle')}</DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
                {dialogError}
              </Alert>
            )}

            {issuedToken ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Alert severity="warning">{t('admin.apiTokens.copyWarning')}</Alert>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    value={issuedToken}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                  />
                  <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'}>
                    <ContentCopyIcon />
                  </IconButton>
                </Box>
                {copied && (
                  <Typography variant="caption" color="success.main">
                    {t('admin.apiTokens.copied')}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  label={t('admin.apiTokens.fieldName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                  size="small"
                  placeholder={t('admin.apiTokens.fieldNamePlaceholder')}
                />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('admin.apiTokens.fieldAbilities')}
                  </Typography>
                  <FormGroup>
                    {API_TOKEN_ABILITIES.map((ability) => (
                      <FormControlLabel
                        key={ability}
                        control={
                          <Checkbox
                            checked={abilities.includes(ability)}
                            onChange={() => toggleAbility(ability)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography component="span" sx={{ fontFamily: 'monospace' }}>
                              {ability}
                            </Typography>
                            <Typography component="span" variant="body2" color="text.secondary">
                              {t(`admin.apiTokens.abilities.${ability.replace(':', '_')}`)}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={saving}>
              {issuedToken ? t('common.close') : t('common.cancel')}
            </Button>
            {!issuedToken && (
              <Button
                variant="contained"
                onClick={handleIssue}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : undefined}
              >
                {saving ? t('common.loading') : t('admin.apiTokens.issue')}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
